
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import axios from 'axios'
import VerificationModal from '@/components/VerificationModal.vue'
import { FETCH_PEOPLE } from '@/store/actions'
import { UPDATE_PERSON, UPDATE_SMS_OPTIN } from '@/store/mutations'
import { isEmailValid, isPhoneNumberValid } from '@/utils/validationUtils'
import countriesData from '@/utils/countryCodes.json'
import { getDisplayName as importedGetDisplayName } from '@/utils'

export const FIND_CONTACT_EVENTS = {
  COMPLETED: 'findContact:Completed'
}

interface FormData {
  email: string
  phoneNumber: string
  notificationsOptin: boolean
  marketingOptin: boolean
}

interface Country {
  name: string
  isoCode: string
  dialCode: string
}

@Component({
  components: { VerificationModal }
})
export default class FindContact extends Vue {
  @Prop({ type: Boolean, default: false }) showing!: boolean

  showStaffOverride = false
  staffCode = ''
  usePhone = false
  countries: Country[] = countriesData
  selectedCountryIso = 'US'
  formData: FormData = {
    email: '',
    phoneNumber: '',
    notificationsOptin: false,
    marketingOptin: false
  }
  formSubmitted = false
  foundPeople: any[] = []
  showVerificationModal = false
  defaultVerificationMethod: 'email' | 'sms' = 'email'

  formConfig = {
    email: {
      id: 'contact-email',
      label: 'Email Address',
      model: 'email',
      placeholder: 'Enter Email Address',
      required: true,
      type: 'email'
    },
    phone: {
      id: 'contact-phone',
      label: 'Phone Number',
      model: 'phoneNumber',
      placeholder: 'Enter Phone Number',
      required: true,
      type: 'tel'
    },
    notificationsOptin: {
      id: 'contact-notifications-optin',
      label: 'I consent to receive notifications and alerts (transactional messages such as order confirmations and delivery updates).',
      name: 'contact-notifications-optin'
    },
    marketingOptin: {
      id: 'contact-marketing-optin',
      // label: 'I consent to receive marketing communications, including promotional messages, offers, and newsletters.',
      label: 'Opt in to get FREE PHOTOS & VIDEOS of your Mission Experience via SMS. By opting in, you agree to receive text messages from Beat The Bomb and third parties acting on our behalf. You can unsubscribe at any time.',
      name: 'contact-marketing-optin'
    },
    submit: {
      text: 'Verify',
      variant: 'outline-primary'
    }
  }

  get selectedCountry(): Country {
    const c = this.countries.find(x => x.isoCode === this.selectedCountryIso)
    return c || this.countries[0]
  }

  get countryOptions() {
    return [
      { value: '', text: 'Select a country' },
      ...this.countries.map(c => ({
        value: c.isoCode,
        text: this.getFlagEmoji(c.isoCode) + ' ' + c.name
      }))
    ]
  }

  get fullPhoneNumber(): string {
    return this.selectedCountry.dialCode + this.formData.phoneNumber
  }

  get canSubmit(): boolean {
    if (!this.usePhone) {
      let can_can = isEmailValid(this.formData.email)
        && isPhoneNumberValid(this.fullPhoneNumber)
        && !!this.selectedCountryIso;
      console.log(`Can submit? ${can_can}`);
      return can_can;
    } else {
      // TODO: Fix this when we add Twilio support back in.
      return isPhoneNumberValid(this.fullPhoneNumber) && !!this.selectedCountryIso
    }
  }

  get personSelectModalId(): string {
    return 'person-select-modal'
  }

  get emailState(): boolean | null {
    return this.formSubmitted ? isEmailValid(this.formData.email) : null
  }

  get phoneState(): boolean | null {
    return this.formSubmitted ? isPhoneNumberValid(this.fullPhoneNumber) : null
  }

  get countryState(): boolean | null {
    return this.formSubmitted ? !!this.selectedCountryIso : null
  }

  mounted() {
    if (this.showing) {
      this.focusPrimaryInput()
    }
  }

  focusPrimaryInput() {
    if (!this.usePhone) {
      (this.$refs.email as HTMLElement).focus()
    } else {
      (this.$refs.phone as HTMLElement)?.focus()
    }
  }

  toggleStaffOverride() {
    this.showStaffOverride = !this.showStaffOverride
  }

  useEmailOverride() {
    this.showStaffOverride = false
    this.usePhone = false
    this.defaultVerificationMethod = 'email'
  }

  usePhoneOverride() {
    this.showStaffOverride = false
    this.usePhone = true
    this.defaultVerificationMethod = 'sms'
  }

  async useMasterCode() {
    try {
      const r = await axios.post(`${process.env.VUE_APP_API}/verification/master-override`, {
        masterCode: this.staffCode
      })
      if (r.data.success) {
        this.$emit(FIND_CONTACT_EVENTS.COMPLETED)
      } else {
        alert('Invalid master code.')
      }
    } catch {
      alert('Error with master override')
    }
  }

  switchToPhone() {
    this.usePhone = true
    this.defaultVerificationMethod = 'sms'
    this.focusPrimaryInput()
  }

  switchToEmail() {
    this.usePhone = false
    this.defaultVerificationMethod = 'email'
    this.focusPrimaryInput()
  }

  onCountryChange(_val: string) {
    this.formData.phoneNumber = ''
  }

  getFlagEmoji(countryCode: string): string {
    return countryCode
      .toUpperCase()
      .replace(/./g, c => String.fromCodePoint(127397 + c.charCodeAt(0)))
  }

  async onClickVerify() {
    this.formSubmitted = true
    if (!this.canSubmit) return
    if (!(this.$refs.contactForm as HTMLFormElement).checkValidity()) return

    try {
      const previouslyVerified = await this.$store.dispatch(FETCH_PEOPLE, {
        email: !this.usePhone ? this.formData.email : undefined,
        phone: this.usePhone ? this.fullPhoneNumber : undefined
      })
      if (previouslyVerified && previouslyVerified.length) {
        this.foundPeople = previouslyVerified
        this.$bvModal.show(this.personSelectModalId)
        return
      }
    } catch {
      this.formSubmitted = false;
    }

    this.$store.commit(UPDATE_SMS_OPTIN, {
      notification_optin: this.formData.notificationsOptin,
      marketing_optin: this.formData.marketingOptin
    })

    const method = this.usePhone ? 'sms' : 'email'
    try {
      await axios.post(`${process.env.VUE_APP_API}/verification/send-code`, {
        method,
        email: this.usePhone ? undefined : this.formData.email,
        phone: this.usePhone ? this.fullPhoneNumber : undefined
      })
      this.showVerificationModal = true
    } catch {
      alert('Error sending verification code')
      this.formSubmitted = false;
    }
  }

  async resendCode() {
    const method = this.usePhone ? 'sms' : 'email'
    try {
      await axios.post(`${process.env.VUE_APP_API}/verification/send-code`, {
        method,
        email: this.usePhone ? undefined : this.formData.email,
        phone: this.usePhone ? this.fullPhoneNumber : undefined
      })
      alert('Code resent')
    } catch {
      alert('Error resending code')
    }
  }

  handleGoBack() {
    this.showVerificationModal = false;
    this.formSubmitted = false;
  }

  async handleVerificationComplete() {
    this.showVerificationModal = false
    try {
      const result = await this.$store.dispatch(FETCH_PEOPLE, {
        email: this.formData.email,
        phone: this.fullPhoneNumber
      })
      if (result && result.length) {
        this.foundPeople = result
        this.$bvModal.show(this.personSelectModalId)
      } else {
        this.$store.commit(UPDATE_PERSON, {
          ...this.formData,
          phone: this.fullPhoneNumber
        })
        this.formCompleted()
      }
    } catch {
      alert('Error verifying user')
    }
  }

  formCompleted() {
    this.$emit(FIND_CONTACT_EVENTS.COMPLETED)
  }

  selectPersonFromList(person: any) {
    this.$store.commit(UPDATE_PERSON, {
      ...person,
      email: this.formData.email,
      phone: this.fullPhoneNumber
    })
    this.formCompleted()
  }

  getDisplayName(person: any) {
    return importedGetDisplayName(person)
  }

  obfuscateDob(dob: string) {
    if (!dob) return 'N/A'
    const mmdd = dob.slice(-5)
    return mmdd + '-****'
  }

  obfuscatePhone(phone: string) {
    if (!phone) return 'N/A'
    return '***-***-' + phone.slice(-4)
  }

  formatPhoneNumber() {
    this.formData.phoneNumber = this.formData.phoneNumber.replace(/\D/g, '')
  }
}
